// npm
import React from 'react'
import PropTypes from 'prop-types'
import {connect} from 'react-redux'

// material-ui
import Tabs from '@material-ui/core/Tabs'
import Tab from '@material-ui/core/Tab'
import withStyles from '@material-ui/core/styles/withStyles'

// react
import {Workings} from './workings'
import {DrawingCanvas} from './drawing/drawing-canvas'
import {CodeEditor} from './code/code-editor'

// redux (selectors)
import {getExamData} from 'redux/reducers/selectors'
import {getContentData, getFormRunGuid} from 'redux/reducers/exam/selectors'
import {requiresWorkings} from 'redux/reducers/exam/content/complex-selectors'
import {getQuestionDataById} from 'redux/reducers/exam/content/questions/selectors'
import {getWorkingsData, requiresWorkingsDrawing, requiresWorkingsCodeEditor} from 'redux/reducers/exam/content/questions/question/selectors'
import {getCurrentQuestionId, getQuestionsData} from 'redux/reducers/exam/content/selectors'
import {getWorkingsDrawing, getWorkingsImageId, getWorkingsCode} from 'redux/reducers/exam/content/questions/question/workings/selectors'

// redux (actions)
import {saveDrawing, setWorkingsImageId, saveCode} from './actions'


// WorkingsTabs (not connected to styles)
// --------------------------------------------------------------------------

// wraps the existing free-text Workings box with optional "Drawing"/"Code" tabs, driven per
// question by @ShowWorkingsDrawing / @ShowWorkingsCodeEditor. Renders exactly as before (no
// tabs, just the text box) when a question has neither.
class WorkingsTabs extends React.Component {
	constructor(props) {
		super(props);
		this.state = {tab: 0};
		this.codeRef = React.createRef();
		this.handleTabChange = this.handleTabChange.bind(this);
		this.handleDrawingChange = this.handleDrawingChange.bind(this);
		this.handleImageUploaded = this.handleImageUploaded.bind(this);
		this.handleCodeChange = this.handleCodeChange.bind(this);
	}

	componentDidUpdate(prevProps) {
		// the set of tabs can differ per question, so a stale index could point at the wrong
		// tab (or none) - always land on Notes when moving to another question
		if (prevProps.currentQuestionId !== this.props.currentQuestionId && this.state.tab !== 0) {
			this.setState({tab: 0});
		}
	}

	handleTabChange(_, tab) {
		this.setState({tab});
	}

	handleDrawingChange(drawing) {
		this.props.onDrawingChange(drawing);
	}

	handleImageUploaded(imageId) {
		this.props.onImageUploaded(imageId);
	}

	handleCodeChange(code) {
		this.props.onCodeChange(code);
	}

	getPanes() {
		const {showDrawing, showCodeEditor, value, onChange, workingsRef, autoExpand} = this.props;
		const {drawing, code, formRunGuid, currentQuestionId} = this.props;

		const workingsProps = {value, onChange, workingsRef, autoExpand};
		const panes = [{label: 'Notes', content: <Workings {...workingsProps}/>}];

		if (showDrawing) {
			panes.push({
				label: 'Drawing',
				content: (
					<DrawingCanvas
						drawing={drawing}
						onChange={this.handleDrawingChange}
						formRunGuid={formRunGuid}
						questionId={currentQuestionId}
						onImageUploaded={this.handleImageUploaded}
					/>
				)
			});
		}

		if (showCodeEditor) {
			panes.push({
				label: 'Code',
				content: <CodeEditor value={code} onChange={this.handleCodeChange} codeRef={this.codeRef}/>
			});
		}

		return panes;
	}

	render() {
		const {showDrawing, showCodeEditor, value, onChange, workingsRef, autoExpand, classes} = this.props;

		if (!showDrawing && !showCodeEditor) {
			return <Workings value={value} onChange={onChange} workingsRef={workingsRef} autoExpand={autoExpand}/>;
		}

		const {tab} = this.state;
		const panes = this.getPanes();

		return (
			<div className={classes.container}>
				<Tabs value={tab} indicatorColor="primary" textColor="primary" onChange={this.handleTabChange}>
					{panes.map(({label}) => <Tab key={label} label={label}/>)}
				</Tabs>
				{panes.map(({label, content}, index) => (
					<div key={label} className={classes.tabContent} style={{display: tab === index ? 'flex' : 'none'}}>
						{content}
					</div>
				))}
			</div>
		);
	}
}

WorkingsTabs.propTypes = {
	value: PropTypes.string,
	onChange: PropTypes.func.isRequired,
	workingsRef: PropTypes.object,
	autoExpand: PropTypes.bool
};


// WorkingsTabs (connected to styles)
// --------------------------------------------------------------------------

const styles = () => ({
	container: {
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		minWidth: 0,
		minHeight: 0
	},
	tabContent: {
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		minWidth: 0,
		minHeight: 0
	}
});

WorkingsTabs = withStyles(styles)(WorkingsTabs);


// WorkingsTabs (connected to store)
// --------------------------------------------------------------------------

const mapStoreToProps = (store) => {
	const examData = getExamData(store);
	const contentData = getContentData(examData);
	const questionsData = getQuestionsData(contentData);
	const currentQuestionId = getCurrentQuestionId(contentData);
	const questionData = getQuestionDataById(questionsData)(currentQuestionId);
	const workingsData = getWorkingsData(questionData);

	// the extra tabs only ever appear alongside the Notes box, so they're also gated on
	// ShowWorkings itself (a calculator-only panel never gets Drawing/Code tabs)
	const hasWorkings = requiresWorkings(contentData, currentQuestionId);

	return {
		showDrawing: hasWorkings && requiresWorkingsDrawing(questionData),
		showCodeEditor: hasWorkings && requiresWorkingsCodeEditor(questionData),
		drawing: getWorkingsDrawing(workingsData),
		imageId: getWorkingsImageId(workingsData),
		code: getWorkingsCode(workingsData),
		formRunGuid: getFormRunGuid(examData),
		currentQuestionId
	};
};

const mapDispatchToProps = (dispatch) => ({
	onDrawingChange: (questionId) => (drawing) => dispatch(saveDrawing(questionId, drawing)),
	onImageUploaded: (questionId) => (imageId) => dispatch(setWorkingsImageId(questionId, imageId)),
	onCodeChange: (questionId) => (code) => dispatch(saveCode(questionId, code))
});

const mergeProps = (
	{showDrawing, showCodeEditor, drawing, imageId, code, formRunGuid, currentQuestionId},
	{onDrawingChange, onImageUploaded, onCodeChange},
	ownProps
) => ({
	...ownProps,
	showDrawing,
	showCodeEditor,
	drawing,
	imageId,
	code,
	formRunGuid,
	currentQuestionId,
	onDrawingChange: onDrawingChange(currentQuestionId),
	onImageUploaded: onImageUploaded(currentQuestionId),
	onCodeChange: onCodeChange(currentQuestionId)
});

const args = [mapStoreToProps, mapDispatchToProps, mergeProps];
WorkingsTabs = connect(...args)(WorkingsTabs);


// Export
// --------------------------------------------------------------------------
export {WorkingsTabs}
